import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getProgram } from '../../config/programs';
import { fetchMember, resolveAuthorName } from '../../utils/programData';
import {
  FORM_TYPES, BASELINE_HEADER_FIELDS, BASELINE_SECTIONS,
  sectionCompletion, overallCompletion,
} from '../../utils/baselineSchema';
import { fetchBaselineForm, saveBaselineHeader, saveBaselineSection } from '../../utils/baselineData';
import { ProgramShell, S, T, Field, BlockSection } from './ui';

// ══════════════════════════════════════════════════════════════════════
//  BREAKING THE CYCLE — fomu rasmi ya taasisi kwa mtoto wa Msaada.
//  KILA SEHEMU (A–D) INASEVIWA KIVYAKE — hii ndiyo "kusave draft":
//  mtu akijaza sehemu moja tu leo, hakuna kinachopotea; mwingine
//  anaweza kuja kujaza sehemu yake wakati wowote baadaye.
// ══════════════════════════════════════════════════════════════════════

export default function BaselineForm() {
  const { pid, mid } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const program = getProgram(pid);

  const [member, setMember] = useState(null);
  const [formType, setFormType] = useState('baseline');
  const [header, setHeader] = useState({});
  const [values, setValues] = useState({}); // { A: {...}, B: {...}, ... }
  const [sectionMeta, setSectionMeta] = useState({});
  const [completerNames, setCompleterNames] = useState({});
  const [openSection, setOpenSection] = useState('A');
  const [loading, setLoading] = useState(true);
  const [savingSection, setSavingSection] = useState(null);
  const [savingHeader, setSavingHeader] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mid, formType]);

  async function load() {
    setLoading(true);
    try {
      const [m, form] = await Promise.all([
        fetchMember(mid),
        fetchBaselineForm(mid, formType),
      ]);
      setMember(m);
      setHeader(form?.header || {});
      setValues(form?.values || {});
      setSectionMeta(form?.sectionMeta || {});
      const names = {};
      BASELINE_SECTIONS.forEach(s => { names[s.id] = form?.sectionMeta?.[s.id]?.completedByName || ''; });
      setCompleterNames(names);
    } catch (e) { console.error(e); setErr('Imeshindikana kupakia fomu.'); }
    setLoading(false);
  }

  function flash(m) { setMsg(m); setTimeout(() => setMsg(''), 3500); }

  function changeHeader(k, v) { setHeader(h => ({ ...h, [k]: v })); }
  function changeSectionField(sectionId, k, v) {
    setValues(vs => ({ ...vs, [sectionId]: { ...(vs[sectionId] || {}), [k]: v } }));
  }

  async function saveHeader() {
    setSavingHeader(true); setErr('');
    try {
      const authorName = await resolveAuthorName(currentUser);
      await saveBaselineHeader({ memberId: mid, formType, header, author: { uid: currentUser.uid, name: authorName } });
      flash('Taarifa za juu zimehifadhiwa.');
    } catch (e) { console.error(e); setErr('Imeshindikana kuhifadhi taarifa za juu.'); }
    setSavingHeader(false);
  }

  async function saveSection(section) {
    setSavingSection(section.id); setErr('');
    try {
      const authorName = await resolveAuthorName(currentUser);
      await saveBaselineSection(
        mid, formType, section.id, values[section.id] || {},
        completerNames[section.id] || authorName,
        { uid: currentUser.uid, name: authorName },
      );
      setSectionMeta(sm => ({
        ...sm,
        [section.id]: { ...(sm[section.id] || {}), completedByName: completerNames[section.id] || authorName },
      }));
      flash(`Sehemu ${section.id} imehifadhiwa.`);
    } catch (e) { console.error(e); setErr(`Imeshindikana kuhifadhi Sehemu ${section.id}.`); }
    setSavingSection(null);
  }

  if (!program) return <Navigate to="/programs" />;
  if (loading) return <ProgramShell program={program} tab="members"><div style={{ ...S.card, ...S.empty }}>Inapakia...</div></ProgramShell>;
  if (!member) return <ProgramShell program={program} tab="members"><div style={{ ...S.card, ...S.empty }}>Mwanachama hakupatikana.</div></ProgramShell>;

  const overall = overallCompletion(
    BASELINE_SECTIONS.reduce((acc, s) => ({ ...acc, ...(values[s.id] || {}) }), {})
  );

  return (
    <ProgramShell program={program} tab="members">

      <div style={{ ...S.card, borderLeft: `4px solid ${program.color}` }}>
        <div
          onClick={() => navigate(`/programs/${pid}/members/${mid}`)}
          style={{ fontSize: '12px', color: T.muted, cursor: 'pointer', marginBottom: '10px' }}
        >
          ← Wasifu wa {member.fullName}
        </div>
        <div style={{ fontFamily: T.serif, fontSize: '20px', color: T.green }}>
          Breaking the Cycle — Fomu ya Taarifa za Mtoto
        </div>
        <div style={{ fontSize: '12.5px', color: T.muted, marginTop: '5px', lineHeight: 1.7 }}>
          Fomu rasmi ya taasisi kwa {member.fullName}. Kila sehemu inajazwa na mtu tofauti kwa nyakati tofauti —
          hifadhi sehemu moja moja; huna haja ya kumaliza fomu nzima kwa kukaa kimoja.
        </div>

        <div style={{ display: 'flex', gap: '8px', marginTop: '16px', marginBottom: '6px' }}>
          {FORM_TYPES.map(t => (
            <button key={t.key} onClick={() => setFormType(t.key)} style={{
              padding: '9px 16px', borderRadius: '99px', cursor: 'pointer',
              border: `1px solid ${formType === t.key ? program.color : T.line}`,
              background: formType === t.key ? program.color : '#fff',
              color: formType === t.key ? '#fff' : T.muted,
              fontSize: '12.5px', fontWeight: 700, fontFamily: 'inherit',
            }}>{t.label}</button>
          ))}
        </div>

        <div style={{ marginTop: '14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11.5px', color: T.muted, marginBottom: '6px' }}>
            <span>Umekamilika</span><strong style={{ color: overall === 100 ? '#059669' : T.ink }}>{overall}%</strong>
          </div>
          <div style={{ height: '8px', background: '#F0EEE7', borderRadius: '99px', overflow: 'hidden' }}>
            <div style={{
              width: `${overall}%`, height: '100%', borderRadius: '99px',
              background: overall === 100 ? '#059669' : `linear-gradient(90deg, ${program.color}, ${T.greenLight})`,
              transition: 'width .3s',
            }} />
          </div>
        </div>
      </div>

      {msg && <div style={S.ok}>{msg}</div>}
      {err && <div style={S.err}>{err}</div>}

      {/* Header fields */}
      <div style={S.card}>
        <h2 style={S.h2}>Taarifa za Juu</h2>
        {BASELINE_HEADER_FIELDS.map(f => (
          <Field key={f.key} field={f} value={header[f.key]} onChange={changeHeader} color={program.color} />
        ))}
        <button style={{ ...S.btnGhost, opacity: savingHeader ? 0.6 : 1 }} disabled={savingHeader} onClick={saveHeader}>
          {savingHeader ? 'Inahifadhi…' : 'Hifadhi Taarifa za Juu'}
        </button>
      </div>

      {/* Sections A-D */}
      {BASELINE_SECTIONS.map(section => {
        const pct = sectionCompletion(values[section.id] || {}, section);
        return (
          <BlockSection
            key={section.id}
            block={{ label: `${section.title} (${section.enTitle})`, color: section.color, icon: section.icon }}
            open={openSection === section.id}
            onToggle={() => setOpenSection(openSection === section.id ? null : section.id)}
            done={pct}
          >
            <div style={{ fontSize: '11.5px', color: T.muted, marginBottom: '14px', lineHeight: 1.6 }}>
              Inajazwa na: <strong style={{ color: T.ink }}>{section.completerLabel}</strong> ({section.enCompleter})
              {sectionMeta[section.id]?.completedByName && (
                <> · ilihifadhiwa mwisho na <strong style={{ color: T.ink }}>{sectionMeta[section.id].completedByName}</strong></>
              )}
            </div>

            <label style={S.label}>Jina la mtu anayejaza sehemu hii</label>
            <input
              style={{ ...S.input, marginBottom: '16px' }}
              value={completerNames[section.id] || ''}
              onChange={e => setCompleterNames(c => ({ ...c, [section.id]: e.target.value }))}
              placeholder={section.completerLabel}
            />

            {section.fields.map(f => (
              <Field key={f.key} field={f} value={(values[section.id] || {})[f.key]}
                onChange={(k, v) => changeSectionField(section.id, k, v)} color={section.color} />
            ))}

            <button
              style={{
                background: `linear-gradient(135deg, ${section.color}, ${section.color})`, color: '#fff',
                border: 'none', padding: '12px 20px', borderRadius: '11px', fontSize: '13px',
                fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                opacity: savingSection === section.id ? 0.6 : 1,
              }}
              disabled={savingSection === section.id}
              onClick={() => saveSection(section)}
            >
              {savingSection === section.id ? 'Inahifadhi…' : `Hifadhi Sehemu ${section.id}`}
            </button>
          </BlockSection>
        );
      })}

      <div style={{ ...S.card, background: '#FAF8F3', fontSize: '11.5px', color: T.muted, lineHeight: 1.7 }}>
        Siri. Ridhaa ya mzazi imepatikana kwa maswali haya na kwa matumizi ya jumla bila majina kwenye ripoti.
      </div>

    </ProgramShell>
  );
}
